import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import EquipmentInspectionForm from '../../forms/EquipmentInspectionForm';
import { useAuth } from '../../contexts/AuthContext';
import { isFieldSurface } from '../../lib/inspection-surface';

/* ── Equipment inspections save themselves through the casing endpoint ──
 * The form posts to /casing/inspection-submissions (structured types and Tong
 * Truck alike) and, on success, navigates to whichever inspections list belongs
 * to the route tree it was opened in — see lib/inspection-surface.ts. It used
 * to navigate to the office list unconditionally, which on three of the five
 * routes below is a path that tree does not register. This wrapper only
 * supplies the page chrome.
 *
 * ONLY A CASING TENANT GETS THAT FORM. Every type it offers (Tong Truck, Power
 * Tong, CRTi, elevators, slips) is casing equipment, its unit picker reads the
 * casing `equipment` table, and every call it makes goes to `/casing/*`, which
 * the API refuses with a 403 unless the tenant has `casing_running`. A trucking
 * or rentals tenant opening it got the tong-truck checklist, an empty unit
 * picker, and a submit that could never land.
 *
 * The gate is a positive check on casing and nothing else, so a tenant holding
 * casing alongside another vertical still gets this form exactly as before.
 * Everyone else gets NonCasingInspection, which says which inspection belongs
 * to their verticals and where it is recorded, or that it is not built yet. */
export default function InspectionFormPage() {
  const navigate = useNavigate();
  const { hasVertical } = useAuth();
  if (!hasVertical('casing_running')) return <NonCasingInspection />;
  return (
    <div className="animate-fade-in">
      {/* The status-bar inset stays on the wrapper: three of the five routes rendering this
       * page (/field/*, /driver/*) sit outside AppLayout, so nothing above the band clears
       * the notch on native. The hand-rolled header this replaced carried it unconditionally. */}
      <div style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <PageHeader title="Equipment Inspection" />
      </div>
      <div className="px-4 py-4">
        <EquipmentInspectionForm onCancel={() => navigate(-1)} />
      </div>
    </div>
  );
}

/**
 * What a tenant without casing finds behind "Equipment Inspection".
 *
 * One row per vertical the tenant holds, each from an independent positive
 * `hasVertical` check, so a trucking-and-rentals tenant reads both. Rows say
 * what is true today:
 *
 * - Rentals, office: the register at /rentals/inspections. That route exists
 *   only in the office tree, so the field row does not link to it.
 * - Rentals, field: inspections are taken on a delivery or a return, from the
 *   dispatch. There is no standalone rental inspection on the field surface.
 * - Trucking: the daily trip inspection (api#855), filed at
 *   /field/trip-inspections/new on the field surface and at
 *   /trucking/trip-inspections/new in the office — each tree registers its own.
 *
 * A tenant with none of these (downhole tools or midstream alone) is told
 * there is no equipment inspection for it, never shown an empty list.
 */
function NonCasingInspection() {
  const { hasVertical } = useAuth();
  const { pathname } = useLocation();
  const onField = isFieldSurface(pathname);
  const rentals = hasVertical('surface_rentals');
  // Trip inspections serve trucking and surface rentals tenants alike (#815, api#1043).
  const trucking = hasVertical('trucking') || hasVertical('surface_rentals');

  return (
    <div className="animate-fade-in" data-testid="non-casing-inspection">
      <div style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <PageHeader title="Equipment Inspection" />
      </div>
      <div className="px-4 py-4">
        <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border overflow-hidden divide-y divide-fieldiq-border">
          {rentals && (
            <div className="p-4" data-testid="inspection-kind-rentals">
              <h2 className="text-sm font-semibold text-fieldiq-text">Rental equipment</h2>
              {onField ? (
                <p className="mt-1 text-sm text-fieldiq-muted">
                  Pre-delivery and post-return inspections are taken on the delivery or return, from the dispatch.
                </p>
              ) : (
                <>
                  <p className="mt-1 text-sm text-fieldiq-muted">
                    Pre-delivery and post-return inspections are recorded on the rental inspections register.
                  </p>
                  <Link
                    to="/rentals/inspections"
                    className="mt-3 inline-flex text-sm font-semibold text-fieldiq-accent-signal hover:underline"
                  >
                    Open rental inspections
                  </Link>
                </>
              )}
            </div>
          )}
          {trucking && (
            <div className="p-4" data-testid="inspection-kind-trucking">
              <h2 className="text-sm font-semibold text-fieldiq-text">Trucks and trailers</h2>
              <p className="mt-1 text-sm text-fieldiq-muted">The daily trip inspection, against the checklist your country requires.</p>
              <Link
                to={onField ? '/field/trip-inspections/new' : '/trucking/trip-inspections/new'}
                className="mt-3 inline-flex text-sm font-semibold text-fieldiq-accent-signal hover:underline"
              >
                Start a trip inspection
              </Link>
            </div>
          )}
          {!rentals && !trucking && (
            <p className="p-4 text-sm text-fieldiq-muted" data-testid="inspection-kind-none">
              There is no equipment inspection set up for this company.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
